import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Supplier } from '../types';
import { supplierService } from '../services/supplierService';
import SupplierModal from '../components/Supplier/SupplierModal';
import ConfirmationModal from '../components/ConfirmationModal';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Package,
  TrendingUp,
  Users,
  AlertTriangle,
  Mail,
  Phone,
  Filter,
  Grid,
  List
} from 'lucide-react';
import { customToast } from '../utils/customToast';
import { useTitle } from '../hooks/useTitle';

const SuppliersPage: React.FC = () => {
  useTitle('Suppliers');
  const { theme } = useTheme();
  const { user, isLoading: isAuthLoading } = useAuth();
  const [searchParams] = useSearchParams();
  
  // State management
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  
  // Filter and pagination
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    filterType: '',
    filterStatus: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Selection for bulk operations
  const [selectedSuppliers, setSelectedSuppliers] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  // Confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: 'danger' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'danger'
  });
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  
  // Statistics
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    loadSuppliers();
    loadStatistics();
  }, [filters, currentPage, perPage, isAuthLoading, user]);

  const loadSuppliers = useCallback(async () => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    try {
      setLoading(true);
      setError(null);
      const data = await supplierService.getSuppliers();
      
      // Filter data based on filters
      const filtered = (data || []).filter(supplier => {
        const matchesSearch = 
          supplier.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          supplier.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
          supplier.phone?.toLowerCase().includes(filters.search.toLowerCase()) ||
          supplier.contact_person?.toLowerCase().includes(filters.search.toLowerCase());

        const matchesType = !filters.filterType || supplier.supplier_type === filters.filterType;
        const matchesStatus = !filters.filterStatus || 
          (filters.filterStatus === 'active' && supplier.is_active) ||
          (filters.filterStatus === 'inactive' && !supplier.is_active);

        return matchesSearch && matchesType && matchesStatus;
      });
      
      setTotal(filtered.length);
      setTotalPages(Math.ceil(filtered.length / perPage));
      setSuppliers(filtered);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setError('Failed to load suppliers. Please try again.');
      customToast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, perPage, isAuthLoading, user]);

  const loadStatistics = async () => {
    try {
      const data = await supplierService.getSupplierStatistics();
      setStats(data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    // Open modal immediately and fetch full supplier in background
    setSelectedSupplier(supplier);
    setModalMode('edit');
    setIsModalOpen(true);
    supplierService.getSupplier(supplier.id)
      .then(full => setSelectedSupplier(full))
      .catch(err => {
        console.error('Failed to load supplier for edit', err);
        customToast.error('Failed to load supplier details');
      });
  };

  const handleViewSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setModalMode('view');
    setIsModalOpen(true);
    supplierService.getSupplier(supplier.id)
      .then(full => setSelectedSupplier(full))
      .catch(err => {
        console.error('Failed to load supplier for view', err);
        customToast.error('Failed to load supplier details');
      });
  };

  const handleDeleteSupplier = async (supplier: Supplier) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Supplier',
      message: `Are you sure you want to delete ${supplier.name}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await supplierService.deleteSupplier(supplier.id);
          customToast.success('Supplier deleted successfully');
          loadSuppliers();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to delete supplier';
          customToast.error(errorMessage);
        } finally {
          setConfirmationLoading(false);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    const count = selectedSuppliers.length;
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Suppliers',
      message: `Are you sure you want to delete ${count} supplier${count > 1 ? 's' : ''}? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmationLoading(true);
        try {
          await supplierService.bulkDeleteSuppliers(selectedSuppliers);
          customToast.success(`${count} supplier${count > 1 ? 's' : ''} deleted successfully`);
          setSelectedSuppliers([]);
          loadSuppliers();
          setConfirmationModal(prev => ({ ...prev, isOpen: false }));
        } catch (error: any) {
          console.error('Error deleting suppliers:', error);
          customToast.error(error.response?.data?.message || 'Failed to delete suppliers. Please try again.');
        } finally {
          setConfirmationLoading(false);
        }
      }
    });
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedSupplier(null);
  };

  const handleModalSuccess = () => {
    loadSuppliers();
    loadStatistics();
    handleModalClose();
  };

  const handleSelectSupplier = (id: number) => {
    setSelectedSuppliers(prev =>
      prev.includes(id) ? prev.filter(supplierId => supplierId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedSuppliers.length === currentSuppliers.length) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(currentSuppliers.map(s => s.id));
    }
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      filterType: '',
      filterStatus: ''
    });
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * perPage;
  const indexOfFirstItem = indexOfLastItem - perPage;
  const currentSuppliers = suppliers.slice(indexOfFirstItem, indexOfLastItem);

  const getSupplierTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      manufacturer: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      distributor: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      wholesaler: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || colors.other}`}>
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Suppliers
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Manage your suppliers and their product inventory.
            </p>
          </div>
          
          <div className="flex flex-col space-x-2 space-y-2 sm:space-y-0 sm:flex-row sm:items-center">
            {/* View Mode Toggle */}
            <div className={`flex items-center border rounded-xl p-1 transition-all duration-200 ${
              theme === 'dark' ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-white'
            }`}>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'table'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Table View"
              >
                <List size={16} />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 touch-manipulation ${
                  viewMode === 'grid'
                    ? 'bg-blue-500 text-white shadow-md'
                    : theme === 'dark'
                      ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <Grid size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:space-x-3 sm:gap-0">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 rounded-xl border transition-all duration-200 shadow-sm hover:shadow-md touch-manipulation ${
                  theme === 'dark'
                    ? 'border-gray-600 text-gray-300 bg-gray-800 hover:bg-gray-700 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400'
                }`}
                title={showFilters ? 'Hide Filters' : 'Show Filters'}
              >
                <Filter size={16} />
                <span className="text-sm">Filters</span>
              </button>
              
              <button
                onClick={handleAddSupplier}
                className="flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium touch-manipulation"
                title="Add New Supplier"
              >
                <Plus size={16} />
                <span className="text-sm">Add Supplier</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search size={16} className={`absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`} />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className={`w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base rounded-lg sm:rounded-xl border transition-all duration-200 shadow-sm focus:shadow-md touch-manipulation ${
              theme === 'dark'
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500 focus:bg-gray-750'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-gray-50'
            } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20`}
          />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Suppliers</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{total}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <Users className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Active Suppliers</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-green-600`}>{suppliers.filter(s => s.is_active).length}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
              <TrendingUp className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Inactive Suppliers</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-orange-600`}>{suppliers.filter(s => !s.is_active).length}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-orange-100 rounded-lg flex-shrink-0">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className={`p-3 sm:p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className={`text-xs sm:text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Total Products</p>
              {loading ? (
                <div className={`mt-1 sm:mt-2 h-6 sm:h-8 w-20 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ) : (
                <p className={`text-lg sm:text-2xl font-bold text-purple-600`}>{stats?.total_products || 0}</p>
              )}
            </div>
            <div className="p-2 sm:p-3 bg-purple-100 rounded-lg flex-shrink-0">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className={`p-3 sm:p-4 rounded-lg border ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Supplier Type
              </label>
              <select
                value={filters.filterType}
                onChange={(e) => handleFilterChange('filterType', e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">All Types</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="distributor">Distributor</option>
                <option value="wholesaler">Wholesaler</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Status
              </label>
              <select
                value={filters.filterStatus}
                onChange={(e) => handleFilterChange('filterStatus', e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm touch-manipulation ${
                  theme === 'dark'
                    ? 'bg-gray-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={resetFilters}
                className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  theme === 'dark'
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedSuppliers.length > 0 && (
        <div className={`p-3 sm:p-4 rounded-lg border ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <span className={`font-medium text-sm sm:text-base ${theme === 'dark' ? 'text-white' : 'text-blue-900'}`}>
              {selectedSuppliers.length} Supplier(s) selected
            </span>
            
            <button
              onClick={handleBulkDelete}
              className="flex items-center justify-center space-x-1 px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors touch-manipulation"
            >
              <Trash2 size={14} />
              <span>Delete Selected</span>
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      {viewMode === 'table' ? (
        // Table View
        <div className={`rounded-lg border overflow-hidden ${
          theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          {loading ? (
            // Skeleton loading for table view
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className={`p-4 sm:p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'} animate-pulse`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-4 h-4 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    <div className="flex-1 space-y-2">
                      <div className={`h-4 w-32 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`h-3 w-24 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                    <div className="flex gap-2">
                      <div className={`w-8 h-8 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`w-8 h-8 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                      <div className={`w-8 h-8 rounded ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : currentSuppliers.length === 0 ? (
            <div className="text-center py-12">
              <Users className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <h3 className={`mt-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                No suppliers found
              </h3>
              <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                {filters.search || filters.filterType || filters.filterStatus ? 'Try adjusting your filters' : 'Get started by creating a new supplier'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.length === currentSuppliers.length && currentSuppliers.length > 0}
                          onChange={handleSelectAll}
                          className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${
                            theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                          }`}
                        />
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Supplier Name
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Contact Person
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Contact Info
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Type
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Status
                      </th>
                      <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {currentSuppliers.map((supplier) => (
                      <tr 
                        key={supplier.id}
                        className={`${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} transition-colors ${
                          selectedSuppliers.includes(supplier.id) ? 
                          theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50' 
                          : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(supplier.id)}
                            onChange={() => handleSelectSupplier(supplier.id)}
                            className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${
                              theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                            }`}
                          />
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          <div className="font-medium">{supplier.name}</div>
                          {supplier.city && supplier.country && (
                            <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              {supplier.city}, {supplier.country}
                            </div>
                          )}
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                          {supplier.contact_person || '-'}
                        </td>
                        <td className={`px-6 py-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                          <div className="text-sm space-y-1">
                            {supplier.email && (
                              <div className="flex items-center gap-1">
                                <Mail className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                <span className="truncate">{supplier.email}</span>
                              </div>
                            )}
                            {supplier.phone && (
                              <div className="flex items-center gap-1">
                                <Phone className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                <span>{supplier.phone}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getSupplierTypeBadge(supplier.supplier_type)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            supplier.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}>
                            {supplier.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewSupplier(supplier)}
                              className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                              }`}
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditSupplier(supplier)}
                              className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                              }`}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSupplier(supplier)}
                              className={`p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                theme === 'dark' ? 'text-red-400' : 'text-red-600'
                              }`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View for Table */}
              <div className="sm:hidden space-y-3 p-3 sm:p-4">
                {currentSuppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className={`p-3 sm:p-4 border rounded-lg transition-all duration-200 ${
                      theme === 'dark'
                        ? 'border-gray-700 bg-gray-700'
                        : 'border-gray-200 bg-gray-50'
                    } ${selectedSuppliers.includes(supplier.id) ? 
                      theme === 'dark' ? 'ring-2 ring-blue-500' : 'ring-2 ring-blue-400' 
                      : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedSuppliers.includes(supplier.id)}
                          onChange={() => handleSelectSupplier(supplier.id)}
                          className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${
                            theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                          }`}
                        />
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-semibold text-sm truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {supplier.name}
                          </h3>
                          {supplier.city && supplier.country && (
                            <p className={`text-xs truncate ${
                              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {supplier.city}, {supplier.country}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        <button
                          onClick={() => handleViewSupplier(supplier)}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                          }`}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditSupplier(supplier)}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                          }`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSupplier(supplier)}
                          className={`p-1 rounded ${
                            theme === 'dark' ? 'text-red-400' : 'text-red-600'
                          }`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-2 ml-6">
                      {getSupplierTypeBadge(supplier.supplier_type)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        supplier.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {supplier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {supplier.email || supplier.phone && (
                      <div className="mt-2 ml-6 space-y-1">
                        {supplier.email && (
                          <div className="flex items-center gap-1 text-xs">
                            <Mail className={`w-3 h-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>{supplier.email}</span>
                          </div>
                        )}
                        {supplier.phone && (
                          <div className="flex items-center gap-1 text-xs">
                            <Phone className={`w-3 h-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>{supplier.phone}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`px-3 sm:px-6 py-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, total)} of {total} suppliers
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm transition-all ${
                          currentPage === 1
                            ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      <div className={`px-3 py-1 rounded-lg text-sm ${
                        theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-700'
                      }`}>
                        Page {currentPage} of {totalPages}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm transition-all ${
                          currentPage === totalPages
                            ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        // Grid View
        <div className="space-y-3 sm:space-y-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className={`p-4 sm:p-6 rounded-lg border animate-pulse ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <div className="space-y-3">
                    <div className={`h-4 w-32 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-3 w-full rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-3 w-24 rounded ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                </div>
              ))}
            </div>
          ) : currentSuppliers.length === 0 ? (
            <div className="text-center py-12">
              <Users className={`mx-auto h-12 w-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`} />
              <h3 className={`mt-2 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
                No suppliers found
              </h3>
              <p className={`mt-1 text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                {filters.search || filters.filterType || filters.filterStatus ? 'Try adjusting your filters' : 'Get started by creating a new supplier'}
              </p>
            </div>
          ) : (
            <>
              {/* Grid Header with Select All */}
              {currentSuppliers.length > 0 && (
                <div className={`flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 p-3 rounded-lg border ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={selectedSuppliers.length === currentSuppliers.length && currentSuppliers.length > 0}
                      onChange={handleSelectAll}
                      className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 ${
                        theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                      }`}
                    />
                    <span className={`text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Select All ({currentSuppliers.length} items)
                    </span>
                  </div>
                  {selectedSuppliers.length > 0 && (
                    <span className={`text-sm ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                      {selectedSuppliers.length} selected
                    </span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {currentSuppliers.map((supplier) => (
                  <div
                    key={supplier.id}
                    className={`p-4 sm:p-6 rounded-lg border transition-all duration-200 ${
                      theme === 'dark'
                        ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    } ${selectedSuppliers.includes(supplier.id) ? 
                      theme === 'dark' ? 'ring-2 ring-blue-500' : 'ring-2 ring-blue-400' 
                      : ''}`}
                  >
                    {/* Header: Name + Checkbox */}
                    <div className="flex items-start justify-between mb-3 gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(supplier.id)}
                            onChange={() => handleSelectSupplier(supplier.id)}
                            className={`w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0 ${
                              theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300'
                            }`}
                          />
                          <h3 className={`font-semibold truncate ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {supplier.name}
                          </h3>
                        </div>
                        {supplier.city && supplier.country && (
                          <p className={`text-xs ml-6 truncate ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {supplier.city}, {supplier.country}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {getSupplierTypeBadge(supplier.supplier_type)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        supplier.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {supplier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {/* Contact Info */}
                    {supplier.email || supplier.phone && (
                      <div className="mb-3 space-y-1">
                        {supplier.email && (
                          <div className="flex items-center gap-2 text-xs">
                            <Mail className={`w-3 h-3 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={`truncate ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>{supplier.email}</span>
                          </div>
                        )}
                        {supplier.phone && (
                          <div className="flex items-center gap-2 text-xs">
                            <Phone className={`w-3 h-3 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                            <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>{supplier.phone}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Contact Person */}
                    {supplier.contact_person && (
                      <div className={`mb-3 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Contact: {supplier.contact_person}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewSupplier(supplier)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          theme === 'dark'
                            ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        <Eye className="w-4 h-4 inline mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => handleEditSupplier(supplier)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          theme === 'dark'
                            ? 'bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50'
                            : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        }`}
                      >
                        <Pencil className="w-4 h-4 inline mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteSupplier(supplier)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                          theme === 'dark'
                            ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                        }`}
                      >
                        <Trash2 className="w-4 h-4 inline mr-1" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`px-3 sm:px-6 py-4 border-t rounded-lg ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, total)} of {total} suppliers
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm transition-all ${
                          currentPage === 1
                            ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span className="hidden sm:inline">Previous</span>
                      </button>
                      <div className={`px-3 py-1 rounded-lg text-sm ${
                        theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-50 text-gray-700'
                      }`}>
                        Page {currentPage} of {totalPages}
                      </div>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-lg flex items-center gap-1 text-sm transition-all ${
                          currentPage === totalPages
                            ? theme === 'dark' ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : theme === 'dark' ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      <SupplierModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        supplier={selectedSupplier}
        mode={modalMode}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onClose={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
        type={confirmationModal.type || 'danger'}
        isLoading={confirmationLoading}
      />
    </div>
  );
};

export default SuppliersPage;
